import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Building2, 
  Search,
  Clock,
  CheckCircle,
  XCircle,
  MapPin,
  Phone,
  Mail,
  User,
  Eye,
  FileText,
  ImageIcon,
  Globe,
  Calendar,
  Home
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HoldingPublicQuotes() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [token]);

  useEffect(() => {
    filterRequests();
  }, [requests, searchQuery, statusFilter]);

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/stillastjenester/public-quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
      toast.error("Kunne ikke hente forespørsler");
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.contact_person?.toLowerCase().includes(query) ||
        r.company_name?.toLowerCase().includes(query) ||
        r.project_address?.toLowerCase().includes(query) ||
        r.email?.toLowerCase().includes(query)
      );
    }
    
    if (statusFilter !== "all") {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    
    setFilteredRequests(filtered);
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/stillastjenester/public-quotes/${requestId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success("Status oppdatert");
        fetchRequests();
        setShowDetailModal(false);
      }
    } catch (error) {
      toast.error("Kunne ikke oppdatere status");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { label: "Ny", color: "bg-blue-500/20 text-blue-400", icon: Clock },
      contacted: { label: "Kontaktet", color: "bg-yellow-500/20 text-yellow-400", icon: Phone },
      quoted: { label: "Tilbud sendt", color: "bg-purple-500/20 text-purple-400", icon: FileText },
      accepted: { label: "Akseptert", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
      rejected: { label: "Avslått", color: "bg-red-500/20 text-red-400", icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.new;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} border-0`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getSourceBadge = (source) => {
    const sourceConfig = {
      b2b: { label: "Bedrift", color: "bg-indigo-500/20 text-indigo-400", icon: Building2 },
      private: { label: "Privat", color: "bg-orange-500/20 text-orange-400", icon: Home },
      workers: { label: "Jobbsøker", color: "bg-teal-500/20 text-teal-400", icon: User }
    };
    
    const config = sourceConfig[source] || sourceConfig.b2b;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} border-0`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Globe className="w-6 h-6 text-[#23F7DD]" />
              Offentlige Forespørsler
            </h1>
            <p className="text-gray-400">Forespørsler fra nettsiden (SEO-sider)</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge className="bg-blue-500/20 text-blue-400 border-0">
              {requests.filter(r => r.status === "new").length} nye
            </Badge>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-[#0F0F0F] border-white/10">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Søk etter navn, firma, adresse..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px] bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle statuser</SelectItem>
                  <SelectItem value="new">Nye</SelectItem>
                  <SelectItem value="contacted">Kontaktet</SelectItem>
                  <SelectItem value="quoted">Tilbud sendt</SelectItem>
                  <SelectItem value="accepted">Akseptert</SelectItem>
                  <SelectItem value="rejected">Avslått</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Laster...</div>
        ) : filteredRequests.length === 0 ? (
          <Card className="bg-[#0F0F0F] border-white/10">
            <CardContent className="p-12 text-center">
              <Globe className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Ingen forespørsler</h3>
              <p className="text-gray-400">Det er ingen offentlige forespørsler ennå.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => (
              <Card 
                key={request.quote_request_id} 
                className="bg-[#0F0F0F] border-white/10 hover:border-white/20 transition-colors cursor-pointer"
                onClick={() => { setSelectedRequest(request); setShowDetailModal(true); }}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {getSourceBadge(request.source_page)}
                        {getStatusBadge(request.status)}
                        {request.images?.length > 0 && (
                          <Badge className="bg-pink-500/20 text-pink-400 border-0">
                            <ImageIcon className="w-3 h-3 mr-1" />
                            {request.images.length} bilde(r)
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="text-lg font-semibold text-white">
                        {request.contact_person}
                        {request.company_name && (
                          <span className="text-gray-400 font-normal ml-2">({request.company_name})</span>
                        )}
                      </h3>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {request.project_address}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {request.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {request.phone}
                        </span>
                      </div>
                      
                      <p className="text-gray-300 text-sm line-clamp-2">{request.description}</p>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-xs text-gray-500">
                        {formatDate(request.created_at)}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-white/20 text-white hover:bg-white/10"
                        onClick={(e) => { e.stopPropagation(); setSelectedRequest(request); setShowDetailModal(true); }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Se detaljer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#23F7DD]" />
                Forespørsel Detaljer
              </DialogTitle>
            </DialogHeader>
            
            {selectedRequest && (
              <div className="space-y-6">
                {/* Status and Source */}
                <div className="flex items-center gap-2 flex-wrap">
                  {getSourceBadge(selectedRequest.source_page)}
                  {getStatusBadge(selectedRequest.status)}
                </div>
                
                {/* Contact Info */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-400">Kontaktinformasjon</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-[#23F7DD]" />
                      <span>{selectedRequest.contact_person}</span>
                    </div>
                    {selectedRequest.company_name && (
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-[#23F7DD]" />
                        <span>{selectedRequest.company_name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#23F7DD]" />
                      <a href={`mailto:${selectedRequest.email}`} className="text-[#23F7DD] hover:underline">
                        {selectedRequest.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[#23F7DD]" />
                      <a href={`tel:${selectedRequest.phone}`} className="text-[#23F7DD] hover:underline">
                        {selectedRequest.phone}
                      </a>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Project Details */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-400">Prosjektdetaljer</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-[#23F7DD] mt-1" />
                      <span>{selectedRequest.project_address}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <FileText className="w-4 h-4 text-[#23F7DD] mt-1" />
                      <p className="text-gray-300">{selectedRequest.description}</p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Calendar className="w-4 h-4" />
                      Mottatt: {formatDate(selectedRequest.created_at)}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Images */}
                {selectedRequest.images?.length > 0 && (
                  <Card className="bg-white/5 border-white/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-gray-400">Vedlagte bilder</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {selectedRequest.images.map((url, index) => (
                          <a key={index} href={url} target="_blank" rel="noopener noreferrer">
                            <img 
                              src={url} 
                              alt={`Bilde ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border border-white/10 hover:border-[#23F7DD]/50 transition-colors"
                            />
                          </a>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                    onClick={() => updateStatus(selectedRequest.quote_request_id, "contacted")}
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Marker kontaktet
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                    onClick={() => updateStatus(selectedRequest.quote_request_id, "quoted")}
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Tilbud sendt
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                    onClick={() => updateStatus(selectedRequest.quote_request_id, "accepted")}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Akseptert
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    onClick={() => updateStatus(selectedRequest.quote_request_id, "rejected")}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Avslå
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
